import React, { useCallback, useMemo } from "react";
import { StyleSheet, View, Modal, useWindowDimensions } from "react-native";
import { cropZoomSelectionAtom } from "../../atoms/crop-zoom";
import { useAtom } from "jotai";
import { SystemBars } from "react-native-edge-to-edge";
import Controls from "./Controls";
import Crop from "./crop";
import { BACK_COLOR, CONTROLS_HEIGHT } from "./constants";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { Size } from "./utils";

type ImageCropZoomContentProps = {
    imageUrl: string;
    imageSize?: Size;
};

const ImageCropZoomContent: React.FC<ImageCropZoomContentProps> = ({ imageUrl, imageSize }) => {
    let cropFunction:
        | ((quality?: number) => Promise<{
              uri: string;
              width: number;
              height: number;
          }>)
        | undefined = undefined;

    const styles = useStyles();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const smallerSideSize = windowWidth < windowHeight ? windowWidth : windowHeight;

    const [cropZoomSelection, setCropZoomSelection] = useAtom(cropZoomSelectionAtom);

    const setResult = useCallback(
        (uri?: string) => {
            cropZoomSelection?.onResolve?.(uri);
            setCropZoomSelection(undefined);
        },
        [cropZoomSelection, setCropZoomSelection],
    );

    const onCrop = useCallback(async () => {
        if (!cropFunction) {
            return;
        }
        const { uri } = await cropFunction(0.9);
        setResult(uri);
    }, [cropFunction, setResult]);

    const onCancel = useCallback(() => {
        setResult(undefined);
    }, [setResult]);

    const higherImageHeight =
        smallerSideSize - CONTROLS_HEIGHT > smallerSideSize / 1.3
            ? smallerSideSize - CONTROLS_HEIGHT
            : smallerSideSize / 1.3;

    return (
        <View style={styles.root}>
            <Crop
                source={{ uri: imageUrl, size: imageSize }}
                cropShape={"circle"} // rect || circle
                width={smallerSideSize}
                height={higherImageHeight}
                cropArea={{
                    width: smallerSideSize / 1.3, // required
                    height: smallerSideSize / 1.3, // required
                }}
                borderWidth={0} // default 2
                backgroundColor={BACK_COLOR} // default #FFFFFF, use same format
                opacity={0.5} // between 0 and 1, default is 1
                maxZoom={8} // default 3
                resizeMode={"contain"} // default "cover"
                onCrop={(f) => (cropFunction = f)} // returns a function
            />

            {/*
             * This component background is equals to the one seen in the canvas above
             * The height of this component is subtracted from the screen height, so the overlay
             * height is screen height - controls height
             */}
            <Controls uri={imageUrl} onCrop={onCrop} onCancel={onCancel} />
            <SystemBars style="light" />
        </View>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                root: {
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: BACK_COLOR,
                    paddingBottom: CONTROLS_HEIGHT,
                },
                image: {
                    width: "100%",
                    height: "100%",
                },
            }),
        [],
    );
};

export const ImageCropZoomPortal: React.FC = () => {
    const [selection] = useAtom(cropZoomSelectionAtom);
    const originalUrl = selection && selection.originalUrl;
    if (!originalUrl) {
        return null;
    }
    return (
        <Modal
            visible
            animationType="none"
            statusBarTranslucent
            style={{ flex: 1 }}
            presentationStyle="overFullScreen"
            navigationBarTranslucent
        >
            <GestureHandlerRootView>
                {originalUrl ? (
                    <ImageCropZoomContent
                        imageUrl={originalUrl}
                        imageSize={selection.originalSize}
                    />
                ) : null}
            </GestureHandlerRootView>
        </Modal>
    );
};
